<script setup lang="ts">
import { nextTick, reactive, ref } from "vue";
import {
  clearArray,
  deleteArrayItem,
  flushArray,
  randomInt,
  toObjectString,
  withinErrorMarginNumber
} from "@zyzgroup/core-common";
import {
  ArrowShape,
  BallShape,
  Color,
  ConfettiShapeCombo,
  CrossShape,
  CurveShape,
  DrawShape,
  EllipseShape,
  FoxShape,
  HeartShape,
  HeartVariantShape,
  LineShape,
  PointsShape,
  RabbitShape,
  RectShape,
  SegmentShapeCombo,
  Shape,
  ShapeCombo,
  ShipShapeCombo,
  SliderShapeCombo,
  StarShape,
  TextShape,
  Timeline,
  TreeShapeCombo,
  angle,
  animateShapes,
  distance,
  followMouseShape,
  polymorphTween,
  targetToAfterDropShape,
  toDegree
} from "@zyzgroup/core-web";
import Page from "../components/base/Page.vue";
import FPSStats from "../components/fps/FPSStats.vue";
import Box from "../components/base/Box.vue";
import Button from "../components/form/Button.vue";
import InputRange from "../components/form/InputRange.vue";

const SVGShapes = ref([]);
let svgStart = (d) => {};
let svgFrame = (d) => {};

const CanvasShapes = ref([]);
let canvasStart = (d) => {};
let canvasFrame = (d) => {};

import SVGRawString from "@root/assets/svgs/china_blank_province_map.svg";
// import SVGRawString from "@root/assets/svgs/userwork.svg";
const linePicture = new ShapeCombo({
  svg: SVGRawString,
  forceWidth: 300,
  attrs: {
    "stroke-width": "1"
    // stroke: "#ff0000",
    // fill: "none"
  },
  drag: {
    enable: true,
    followSibling: true
  }
});
const linePictureClone = linePicture.clone();

const strokeDashPath = new Shape({
  initD:
    "M62.9 14.9c-25-7.74-56.6 4.8-60.4 24.3-3.73 19.6 21.6 35 39.6 37.6 42.8 6.2 72.9-53.4 116-58.9 65-18.2 191 101 215 28.8 5-16.7-7-49.1-34-44-34 11.5-31 46.5-14 69.3 9.38 12.6 24.2 20.6 39.8 22.9 91.4 9.05 102-98.9 176-86.7 18.8 3.81 33 17.3 36.7 34.6 2.01 10.2.124 21.1-5.18 30.1",
  attrs: {
    stroke: "#ff0000",
    strokeWidth: "4"
  }
});
const totalLength = ref(+strokeDashPath.pathD().getTotalLength().toFixed(0)); // 988
const dasharray = ref(totalLength.value);
const dashoffset = ref(Math.floor(totalLength.value / 2));
strokeDashPath.setAttr({
  strokeDasharray: `${dasharray.value} ${dasharray.value}`,
  strokeDashoffset: `${dashoffset.value}`
});
function changeDasharray(v: number) {
  dasharray.value = v;
  strokeDashPath.setAttr(
    "strokeDasharray",
    `${dasharray.value} ${dasharray.value}`
  );
}
function changeDashoffset(v: number) {
  dashoffset.value = v;
  strokeDashPath.setAttr("strokeDashoffset", `${dashoffset.value}`);
}

const currentType = ref("");
const cases = [
  {
    name: "shapes",
    onClick: () => {
      currentType.value = "shapes";

      const marker = new BallShape({
        ballRadius: 4,
        attrs: {
          fill: "#ff0000"
        }
      });
      const markerClone = marker.clone();

      const line = new LineShape({
        lineLength: 100,
        degree: 0,
        attrs: { stroke: "#0000ff", strokeWidth: "1" }
      });
      line.moveTo(100, 100);
      const lineClone = line.clone() as LineShape;

      const lineDegree = line.clone() as LineShape;
      lineDegree.rotateTo(30);
      const lineDegreeClone = lineDegree.clone() as LineShape;

      const p = line.intersection(lineDegree);
      if (p) {
        marker.moveTo(p.x, p.y);
      }
      const p2 = lineClone.intersection(lineDegreeClone);
      if (p2) {
        markerClone.moveTo(p2.x, p2.y);
      }

      const tree = new TreeShapeCombo({
        attrs: { stroke: "#ff0000", strokeWidth: "1" },
        drag: {
          enable: true
        }
      });
      tree.comboMove(100, 100);
      tree.comboScale(0.8);
      const treeClone = tree.clone();

      const treeRotate = tree.clone();
      treeRotate.setAttr("stroke", "#00ff00");
      treeRotate.comboRotate(30);
      const treeRotateClone = treeRotate.clone();

      const rect = new RectShape({
        x: 100,
        y: 200,
        scale: 0.5,
        degree: 5,
        rectWidth: 100,
        rectHeight: 60,
        rectBorderRadius: 10,
        attrs: {
          fill: "pink",
          stroke: "#ff0000",
          strokeWidth: "1"
        },
        drag: {
          enable: true
        }
        // strokeArmyAnts: true
      });
      const rect2 = rect.clone() as RectShape;
      rect2.update({
        rectBorderRadius: 0
      });
      rect2.moveY(40);
      const rectClone = rect.clone();
      const rect2Clone = rect2.clone();

      const heart = new HeartShape({
        x: 200,
        y: 200,
        scale: 0.5,
        degree: -10,
        attrs: {
          fill: Color.random().toString(),
          stroke: "#ff0000",
          strokeWidth: "3"
        },
        drag: {
          enable: true
        }
        // strokeArmyAnts: true
      });
      const heartClone = heart.clone();

      const heartVariant = new HeartVariantShape({
        x: 220,
        y: 200,
        scale: 0.5,
        degree: 0,
        attrs: {
          fill: Color.random().toString(),
          stroke: "#ff0000",
          strokeWidth: "3"
        },
        drag: {
          enable: true
        }
        // strokeArmyAnts: true
      });
      const heartVariantClone = heartVariant.clone();

      const star = new StarShape({
        x: 240,
        y: 200,
        scale: 1,
        degree: 0,
        attrs: {
          fill: Color.random().toString()
        },
        drag: {
          enable: true
        }
        // strokeArmyAnts: true
      });
      const starClone = star.clone();

      const fox = new FoxShape({
        x: 260,
        y: 200,
        scale: 1,
        degree: 0,
        attrs: {
          fill: "rgb(200,80,80)",
          stroke: "#ff0000",
          strokeWidth: "3"
        },
        drag: {
          enable: true
        }
        // strokeArmyAnts: true
      });
      const foxClone = fox.clone();

      const rabbit = new RabbitShape({
        x: 300,
        y: 200,
        scale: 1,
        degree: 0,
        attrs: {
          fill: Color.random().toString(),
          stroke: "#ff0000",
          strokeWidth: "1"
        },
        drag: {
          enable: true
        }
        // strokeArmyAnts: true
      });
      const rabbitClone = rabbit.clone();

      const cross = new CrossShape({
        x: 320,
        y: 200,
        scale: 1,
        degree: 0,
        attrs: {
          fill: Color.random().toString()
        },
        drag: {
          enable: true
        }
        // strokeArmyAnts: true
      });
      const crossClone = cross.clone();

      const ship = new ShipShapeCombo({
        showFlame: true,
        attrs: {
          stroke: Color.random().toString()
        },
        drag: {
          enable: true
        }
      });
      ship.comboMove(400, 100);
      ship.comboRotate(30);
      ship.comboScale(2);
      const shipClone = ship.clone();

      const segment = new SegmentShapeCombo({
        segmentWidth: 100,
        segmentHeight: 20,
        // segmentCornerRadius: 5
        attrs: {
          stroke: Color.random().toString()
        },
        drag: {
          enable: true
        }
      });
      segment.comboMove(200, 100);
      segment.comboRotate(0);
      segment.comboScale(1);
      const segmentRotate = segment.clone();
      segmentRotate.comboRotate(30);
      const segmentClone = segment.clone();
      const segmentRotateClone = segmentRotate.clone();

      const slider = new SliderShapeCombo({
        sliderMin: 0,
        sliderMax: 200,
        sliderStep: 2,
        sliderDefaultValue: 100,
        sliderWidth: 200
      });
      slider.comboMove(350, 200);
      // slider.comboRotate(30);
      slider.value$.subscribe((v) => {
        console.log("slider : " + v);
      });
      const sliderClone = slider.clone() as SliderShapeCombo;
      sliderClone.value$.subscribe((v) => {
        console.log("sliderClone : " + v);
      });

      const ellipse = new EllipseShape({
        x: 500,
        y: 100,
        rx: 50,
        ry: 10,
        drag: {
          enable: true
        },
        attrs: {
          fill: Color.random().toString()
        }
      });
      const ellipseClone = ellipse.clone();

      const text = new TextShape({
        x: 500,
        y: 140,
        // text: "你好世界！Hello World!",
        text: "你好世界！\nHello World!",
        drag: {
          enable: true
        },
        attrs: {
          fill: Color.random().toString()
        }
      });
      const textClone = text.clone();

      const curve = new CurveShape({
        attrs: {
          stroke: "#00ff00",
          "stroke-width": "2"
        },
        drag: {
          enable: true
        },
        isLineThrough: false,
        x: 0,
        y: 0,
        toX: 100,
        toY: 0,
        cx: 50,
        cy: -50
      });
      curve.moveTo(300, 200);
      const curveClone = curve.clone();

      SVGShapes.value = [
        line,
        lineDegree,
        tree,
        treeRotate,
        rect,
        rect2,
        heart,
        heartVariant,
        star,
        fox,
        rabbit,
        cross,
        ship,
        segment,
        segmentRotate,
        slider,
        ellipse,
        text,
        curve,
        marker
      ];
      CanvasShapes.value = [
        lineClone,
        lineDegreeClone,
        treeClone,
        treeRotateClone,
        rectClone,
        rect2Clone,
        heartClone,
        heartVariantClone,
        starClone,
        foxClone,
        rabbitClone,
        crossClone,
        shipClone,
        segmentClone,
        segmentRotateClone,
        sliderClone,
        ellipseClone,
        textClone,
        curveClone,
        markerClone
      ];

      svgStart = (d) => {};
      canvasStart = (d) => {};
      svgFrame = (d) => {};
      canvasFrame = (d) => {};
    }
  },
  {
    name: "曲线",
    onClick: () => {
      currentType.value = "曲线";
      const gap = 50;
      const shape1SVG = new CurveShape({
        attrs: {
          stroke: "#ff0000",
          "stroke-width": "2"
        },
        isLineThrough: false
      });
      const shape2SVG = new CurveShape({
        attrs: {
          stroke: "#00ff00",
          "stroke-width": "2"
        },
        isLineThrough: true
      });
      const shape1Canvas = new CurveShape({
        attrs: {
          stroke: "#ff0000",
          "stroke-width": "2"
        },
        isLineThrough: false
      });
      const shape2Canvas = new CurveShape({
        attrs: {
          stroke: "#00ff00",
          "stroke-width": "2"
        },
        isLineThrough: true
      });
      SVGShapes.value = [shape1SVG, shape2SVG];
      CanvasShapes.value = [shape1Canvas, shape2Canvas];
      svgStart = (d) => {
        const x = gap;
        const y = d.rect.height - gap;
        shape1SVG.update({
          toX: d.rect.width - gap * 2,
          toY: 0
        });
        shape1SVG.moveTo(x, y);
        shape2SVG.update({
          toX: d.rect.width - gap * 2,
          toY: 0
        });
        shape2SVG.moveTo(x, y);
      };
      canvasStart = (d) => {
        const x = gap;
        const y = d.rect.height - gap;
        shape1Canvas.update({
          toX: d.rect.width - gap * 2,
          toY: 0
        });
        shape1Canvas.moveTo(x, y);
        shape2Canvas.update({
          toX: d.rect.width - gap * 2,
          toY: 0
        });
        shape2Canvas.moveTo(x, y);
      };
      svgFrame = (d) => {
        shape1SVG.update({
          cx: d.mpg.current.x - shape1SVG.x,
          cy: d.mpg.current.y - shape1SVG.y
        });
        shape2SVG.update({
          cx: d.mpg.current.x - shape2SVG.x,
          cy: d.mpg.current.y - shape2SVG.y
        });
      };
      canvasFrame = (d) => {
        shape1Canvas.update({
          cx: d.mpg.current.x - shape1Canvas.x,
          cy: d.mpg.current.y - shape1Canvas.y
        });
        shape2Canvas.update({
          cx: d.mpg.current.x - shape2Canvas.x,
          cy: d.mpg.current.y - shape2Canvas.y
        });
      };
    }
  },
  {
    name: "多边形",
    onClick: () => {
      currentType.value = "多边形";
      const points: { x: number; y: number }[] = [];
      const ps = new PointsShape({
        attrs: { stroke: "#ff0000", fill: "rgba(0,0,0,0.1)" },
        drag: { enable: true },
        isStraight: true
      });
      const psClone = ps.clone() as PointsShape;
      psClone.update({
        isStraight: false
      });
      SVGShapes.value = [ps];
      CanvasShapes.value = [psClone];
      svgStart = (d) => {
        const nums = 10;
        for (let i = 0; i < nums; i++) {
          const point = {
            x: Math.random() * d.rect.width,
            y: Math.random() * d.rect.height
          };
          points.push(point);
          const rect = new RectShape({
            x: point.x,
            y: point.y,
            rectWidth: 6,
            rectHeight: 6,
            attrs: {
              fill: i == 0 ? "#ff0000" : i == nums - 1 ? "#00ff00" : "#666666"
            }
          });
          SVGShapes.value.unshift(rect);
          CanvasShapes.value.unshift(rect);
        }
        ps.update({ points });
        psClone.update({ points });
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {};
      canvasFrame = (d) => {};
    }
  },
  {
    name: "followMouse",
    onClick: () => {
      currentType.value = "followMouse";
      const arrow = new ArrowShape({
        attrs: {
          fill: Color.random().toString()
        },
        scale: 0.5
      });
      const arrowClone = arrow.clone();
      const arrowLine = new LineShape({
        lineLength: 0,
        attrs: {
          "stroke-width": "1",
          stroke: "#666666"
        }
      });
      const arrowLineClone = arrowLine.clone() as LineShape;
      SVGShapes.value = [arrowLine, arrow];
      CanvasShapes.value = [arrowLineClone, arrowClone];
      svgStart = (d) => {
        const centerX = d.rect.width * 0.5;
        const centerY = d.rect.height * 0.5;
        arrowLine.moveTo(centerX, centerY);
      };
      canvasStart = (d) => {
        const centerX = d.rect.width * 0.5;
        const centerY = d.rect.height * 0.5;
        arrowLineClone.moveTo(centerX, centerY);
      };
      svgFrame = (d) => {
        followMouseShape(arrow, d.mpg);
        arrowLine.lineToAbsolutePoint(arrow);
      };
      canvasFrame = (d) => {
        followMouseShape(arrowClone, d.mpg);
        arrowLineClone.lineToAbsolutePoint(arrowClone);
      };
    }
  },
  {
    name: "dragBall",
    onClick: () => {
      currentType.value = "dragBall";
      const dragBall = new BallShape({
        ballRadius: 30,
        attrs: {
          fill: Color.random().toString()
        },
        drag: {
          enable: true
        }
      });
      const dragBallClone = dragBall.clone();
      SVGShapes.value = [dragBall];
      CanvasShapes.value = [dragBallClone];
      const centerSVG = { x: 0, y: 0 };
      const centerCanvas = { x: 0, y: 0 };
      svgStart = (d) => {
        const centerX = d.rect.width * 0.5;
        const centerY = d.rect.height * 0.5;
        dragBall.moveTo(centerX, centerY);
        centerSVG.x = centerX;
        centerSVG.y = centerY;
      };
      canvasStart = (d) => {
        const centerX = d.rect.width * 0.5;
        const centerY = d.rect.height * 0.5;
        dragBallClone.moveTo(centerX, centerY);
        centerCanvas.x = centerX;
        centerCanvas.y = centerY;
      };
      svgFrame = (d) => {
        targetToAfterDropShape(dragBall, centerSVG);
      };
      canvasFrame = (d) => {
        targetToAfterDropShape(dragBallClone, centerCanvas);
      };
    }
  },
  {
    name: "球体碰撞检测",
    onClick: () => {
      currentType.value = "球体碰撞检测";
      const ball1 = new BallShape({
        ballRadius: 30,
        attrs: {
          fill: Color.random().toString()
        },
        mass: 2,
        vx: 2
      });
      const ball2 = new BallShape({
        ballRadius: 30,
        attrs: {
          fill: Color.random().toString()
        },
        mass: 1,
        vx: -2
      });
      SVGShapes.value = [ball1, ball2];
      CanvasShapes.value = [ball1, ball2];
      svgStart = (d) => {
        const centerY = d.rect.height * 0.5;
        ball1.moveTo(50, centerY);
        ball2.moveTo(d.rect.width - 50, centerY);
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {
        // ball1.checkCollision(ball2);
        // ball1.animate();
        // ball2.animate();
        animateShapes([ball1, ball2], { enableCollision: true });
      };
      canvasFrame = (d) => {};
    }
  },
  {
    name: "球体回弹检测",
    onClick: () => {
      currentType.value = "球体回弹检测";
      const numBalls = 15;
      const balls: BallShape[] = [];
      for (let i = 0; i < numBalls; i++) {
        const ballRadius = Math.random() * 20 + 15;
        const ball = new BallShape({
          ballRadius,
          attrs: {
            fill: Color.random().toString()
          },
          mass: ballRadius,
          vx: Math.random() * 10 - 5,
          vy: Math.random() * 10 - 5
        });
        balls.push(ball);
      }
      const wall = {
        x: 0,
        y: 0,
        width: 0,
        height: 0
      };
      SVGShapes.value = [...balls];
      CanvasShapes.value = [...balls];
      svgStart = (d) => {
        balls.forEach((b) => {
          b.moveTo(Math.random() * d.rect.width, Math.random() * d.rect.height);
        });
        wall.width = d.rect.width;
        wall.height = d.rect.height;
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {
        animateShapes(balls, { enableCollision: true, wall });
      };
      canvasFrame = (d) => {};
    }
  },
  {
    name: "物体引力",
    onClick: () => {
      currentType.value = "物体引力";
      const sun = new BallShape({
        ballRadius: 50,
        attrs: {
          fill: Color.random().toString()
        },
        mass: 10000
      });
      sun.mass = 10000;
      const planet = new BallShape({
        ballRadius: 10,
        attrs: {
          fill: Color.random().toString()
        },
        mass: 1,
        vy: 7
      });
      SVGShapes.value = [sun, planet];
      CanvasShapes.value = [sun, planet];
      svgStart = (d) => {
        const centerX = d.rect.width * 0.5;
        const centerY = d.rect.height * 0.5;
        sun.moveTo(centerX, centerY);
        planet.moveTo(centerX + 200, centerY);
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {
        animateShapes(SVGShapes.value, { enableGravityBetweenObjects: true });
      };
      canvasFrame = (d) => {};
    }
  },
  {
    name: "万有引力",
    onClick: () => {
      currentType.value = "万有引力";
      const numBalls = 30;
      const balls: BallShape[] = [];
      for (let i = 0; i < numBalls; i++) {
        const radius = Math.random() * 25 + 5;
        const ball = new BallShape({
          ballRadius: radius,
          attrs: {
            fill: Color.random().toString()
          },
          mass: radius
        });
        balls.push(ball);
      }
      SVGShapes.value = [...balls];
      CanvasShapes.value = [...balls];
      svgStart = (d) => {
        balls.forEach((b, i) => {
          b.moveTo(Math.random() * d.rect.width, Math.random() * d.rect.height);
        });
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {
        animateShapes(balls, {
          enableCollision: true,
          enableGravityBetweenObjects: true
        });
      };
      canvasFrame = (d) => {};
    }
  },
  {
    name: "Spring1",
    onClick: () => {
      currentType.value = "Spring1";
      const lines: LineShape[] = [];
      const balls: BallShape[] = [];
      for (let i = 0; i < 3; i++) {
        lines.push(
          new LineShape({ attrs: { stroke: "#666666" }, interactive: false })
        );
        balls.push(
          new BallShape({
            ballRadius: 10,
            attrs: {
              fill: Color.random().toString()
            },
            drag: {
              enable: true
            }
          })
        );
      }
      const point = new BallShape({
        ballRadius: 20,
        attrs: {
          fill: "#ff0000"
        }
      });
      SVGShapes.value = [...lines, ...balls, point];
      CanvasShapes.value = [...lines, ...balls, point];
      svgStart = (d) => {
        balls.forEach((handle) => {
          handle.moveTo(
            Math.random() * d.rect.width,
            Math.random() * d.rect.height
          );
        });
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {
        point.animateSpringTo(balls, {
          spring: 0.03
        });
        balls.forEach((handle, index) => {
          lines[index].moveTo(point.x, point.y);
          lines[index].lineToAbsolutePoint(handle);
        });
      };
      canvasFrame = (d) => {};
    }
  },
  {
    name: "Spring2",
    onClick: () => {
      currentType.value = "Spring2";
      const lines: LineShape[] = [];
      const balls: BallShape[] = [];
      for (let i = 0; i < 3; i++) {
        lines.push(
          new LineShape({ attrs: { stroke: "#666666" }, interactive: false })
        );
        balls.push(
          new BallShape({
            ballRadius: 20,
            attrs: {
              fill: Color.random().toString()
            },
            drag: {
              enable: i == 0
            },
            interactive: i == 0
          })
        );
      }
      SVGShapes.value = [...lines, ...balls];
      CanvasShapes.value = [...lines, ...balls];
      svgStart = (d) => {
        balls.forEach((b) => {
          b.moveTo(d.rect.width / 2, d.rect.height / 2);
        });
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {
        animateShapes(balls, {
          spring: 0.03,
          friction: 0.9,
          gravity: 2
        });
        balls.forEach((ballB, i) => {
          if (i === 0) {
            lines[i].moveTo(d.mpg.x, d.mpg.y);
            lines[i].lineToAbsolutePoint(ballB);
          } else {
            const ballA = balls[i - 1];
            lines[i].moveTo(ballA.x, ballA.y);
            lines[i].lineToAbsolutePoint(ballB);
          }
        });
      };
      canvasFrame = (d) => {};
    }
  },
  {
    name: "SpringConnect",
    onClick: () => {
      currentType.value = "SpringConnect";
      const lines: LineShape[] = [];
      const balls: BallShape[] = [];
      for (let i = 0; i < 3; i++) {
        lines.push(
          new LineShape({ attrs: { stroke: "#666666" }, interactive: false })
        );
        balls.push(
          new BallShape({
            ballRadius: 10,
            attrs: {
              fill: Color.random().toString()
            },
            drag: {
              enable: true
            }
          })
        );
      }
      SVGShapes.value = [...lines, ...balls];
      CanvasShapes.value = [...lines, ...balls];
      svgStart = (d) => {
        balls.forEach((b) => {
          b.moveTo(Math.random() * d.rect.width, Math.random() * d.rect.height);
        });
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {
        animateShapes(balls, {
          springLength: 120
        });
        lines[0].moveTo(balls[0].x, balls[0].y);
        lines[0].lineToAbsolutePoint(balls[1]);
        lines[1].moveTo(balls[1].x, balls[1].y);
        lines[1].lineToAbsolutePoint(balls[2]);
        lines[2].moveTo(balls[2].x, balls[2].y);
        lines[2].lineToAbsolutePoint(balls[0]);
      };
      canvasFrame = (d) => {};
    }
  },
  {
    name: "喷泉",
    onClick: () => {
      currentType.value = "喷泉";
      const numBalls = 80;
      const balls: BallShape[] = [];
      for (let i = 0; i < numBalls; i++) {
        const ball = new BallShape({
          ballRadius: 2,
          attrs: {
            fill: Color.random().toString()
          },
          vx: Math.random() * 2 - 1, // [-1,1]
          vy: Math.random() * -10 - 10 // [-20,-10]
        });
        balls.push(ball);
      }
      SVGShapes.value = [...balls];
      CanvasShapes.value = [...balls];
      svgStart = (d) => {
        balls.forEach((b, i) => {
          b.moveTo(d.rect.width * 0.5, d.rect.height);
        });
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {
        animateShapes(balls, { gravity: 0.5 });
        balls.forEach((ball) => {
          if (
            ball.x - ball.ballRadius > d.rect.width ||
            ball.x + ball.ballRadius < 0 ||
            ball.y - ball.ballRadius > d.rect.height ||
            ball.y + ball.ballRadius < 0
          ) {
            ball.moveTo(d.rect.width * 0.5, d.rect.height);
            ball.vx = Math.random() * 2 - 1;
            ball.vy = Math.random() * -10 - 10;
          }
        });
      };
      canvasFrame = (d) => {};
    }
  },
  {
    name: "俄罗斯方块",
    onClick: () => {
      currentType.value = "俄罗斯方块";
      SVGShapes.value = [];
      CanvasShapes.value = [];
      const boxes: RectShape[] = [];
      function createBox(width: number) {
        const box = new RectShape({
          x: Math.random() * width,
          y: 0,
          rectWidth: 50,
          rectHeight: 50,
          attrs: {
            fill: Color.random().toString()
          },
          drag: {
            enable: true
          }
        });
        boxes.push(box);
        SVGShapes.value.push(box);
        CanvasShapes.value.push(box);
        // SVGShapes.value = [...boxes];
        // CanvasShapes.value = [...boxes];
        return box;
      }
      let activeBox: RectShape;
      svgStart = (d) => {
        activeBox = createBox(d.rect.width);
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {
        if (d.mpg.current.isDown) {
          // drag:暂停
        } else {
          if (activeBox) {
            activeBox.animate({ gravity: 0.1 });
          }
          // 触底
          if (
            activeBox &&
            activeBox.y + activeBox.rectHeight >= d.rect.height
          ) {
            activeBox.y = d.rect.height - activeBox.rectHeight;
            activeBox = createBox(d.rect.width);
          }
          boxes.forEach((box) => {
            // 相碰
            if (activeBox && activeBox !== box && activeBox.intersection(box)) {
              activeBox.y = box.y - activeBox.rectHeight;
              if (activeBox.y >= activeBox.rectHeight) {
                activeBox = createBox(d.rect.width);
              } else {
                activeBox = null;
              }
            }
          });
        }
      };
      canvasFrame = (d) => {};
    }
  },
  {
    name: "物体距离",
    onClick: () => {
      currentType.value = "物体距离";
      const springMassBalls: BallShape[] = [];
      const springMassLines: LineShape[] = [];
      const color0 = new Color("#000000");
      const numParticles = 30;
      for (let i = 0; i < numParticles; i++) {
        const size = Math.random() * 10 + 2;
        const particle = new BallShape({
          ballRadius: size,
          attrs: { fill: Color.random().toString() },
          interactive: false,
          vx: Math.random() * 6 - 3,
          vy: Math.random() * 6 - 3,
          mass: size
        });
        springMassBalls.push(particle);
      }
      SVGShapes.value = [...springMassLines, ...springMassBalls];
      CanvasShapes.value = [...springMassLines, ...springMassBalls];
      svgStart = (d) => {
        springMassBalls.forEach((b, i) => {
          b.moveTo(Math.random() * d.rect.width, Math.random() * d.rect.height);
        });
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {
        animateShapes(springMassBalls, {
          wall: {
            x: 0,
            y: 0,
            width: d.rect.width,
            height: d.rect.height,
            cycle: true
          },
          // spring: 0.0005, 参数替换?
          enableSpringMassAlpha: true
        }).forEach(([fromX, fromY, toX, toY, alpha], i) => {
          if (!springMassLines[i]) {
            const l = new LineShape({
              x: fromX,
              y: fromY,
              lineLength: distance(fromX, fromY, toX, toY),
              degree: toDegree(angle(fromX, fromY, toX, toY)),
              attrs: {
                strokeWidth: "1",
                stroke: color0.setAlpha(alpha).toString()
              },
              interactive: false
            });
            springMassLines.push(l);
            SVGShapes.value.unshift(l);
            CanvasShapes.value.unshift(l);
            // SVGShapes.value = [l, ...SVGShapes.value];
            // CanvasShapes.value = [l, ...CanvasShapes.value];
          } else {
            springMassLines[i].moveTo(fromX, fromY);
            springMassLines[i].lineToAbsolutePoint({ x: toX, y: toY });
            springMassLines[i].setAttr(
              "stroke",
              color0.setAlpha(alpha).toString()
            );
          }
        });
      };
      canvasFrame = (d) => {};
    }
  },
  {
    name: "generateRosetta",
    onClick: () => {
      currentType.value = "generateRosetta";
      const rosetta = Shape.generateRosetta(120, 120, 55, 35, 6);
      const rect = new RectShape({
        x: 0,
        y: 0,
        rectWidth: 20,
        rectHeight: 10,
        attrs: {
          stroke: "none",
          fill: "rgba(0,255,0,0.2)"
        }
      });
      const trail = new Shape({
        attrs: {
          stroke: "red",
          "stroke-width": "3",
          fill: "none"
        }
      });
      SVGShapes.value = [trail, rect];
      CanvasShapes.value = [trail, rect];
      svgStart = (d) => {
        const tl = new Timeline({ frameDuration: 15000 });
        const st = 0.5;
        const et = 1;
        tl.addTween(
          { progress: st },
          { progress: et },
          {
            onUpdate: (obj) => {
              const { point, subPathD } = rosetta.trailPath(st, obj.progress);
              trail.pathD(subPathD);
              rect.moveTo(point.x, point.y);
              rect.rotateTo(point.alpha + 90);
            }
          }
        );
        tl.play();
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {};
      canvasFrame = (d) => {};
    }
  },
  {
    name: "Confetti",
    onClick: () => {
      currentType.value = "Confetti";
      if (
        SVGShapes.value.findIndex((s) => s instanceof ConfettiShapeCombo) < 0
      ) {
        SVGShapes.value = [];
        CanvasShapes.value = [];
      } else {
        SVGShapes.value = [...SVGShapes.value];
        CanvasShapes.value = [...CanvasShapes.value];
      }
      svgStart = (d) => {
        function fire(particleRatio: number, opts?: Record<string, any>) {
          return new ConfettiShapeCombo({
            shapeCreator: () =>
              new RectShape({ rectWidth: 10, rectHeight: 10 }),
            // shapeCreator: () => new BallShape({ ballRadius: 5 }),
            // shapeCreator: () => new HeartShape({ heartRx: 3, heartRy: 3 }),
            width: d.rect.width,
            height: d.rect.height,
            y: 0.7,
            particleCount: Math.floor(200 * particleRatio),
            ...opts
          });
        }
        // const confetti = fire(0.5, {
        //   particleCount: randomInt(50, 100),
        //   spread: 70,
        //   y: 0.6
        // });
        const confetti = fire(1, {
          y: 0.6,
          particleCount: randomInt(50, 100),
          angle: randomInt(55, 125),
          spread: randomInt(50, 70)
        });
        // const confetti = fire(0.25, {
        //   spread: 26,
        //   startVelocity: 55
        // });
        // const confetti = fire(0.2, {
        //   spread: 60
        // });
        // const confetti = fire(0.35, {
        //   spread: 100,
        //   decay: 0.91,
        //   scalar: 0.8
        // });
        // const confetti = fire(0.1, {
        //   spread: 120,
        //   startVelocity: 25,
        //   decay: 0.92,
        //   scalar: 1.2
        // });
        // const confetti = fire(0.1, {
        //   spread: 120,
        //   startVelocity: 45
        // });
        const confettiClone = confetti.clone();
        SVGShapes.value.push(confetti);
        CanvasShapes.value.push(confettiClone);
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {
        const deletes = [];
        SVGShapes.value.forEach((s) => {
          if (s instanceof ConfettiShapeCombo) {
            if (!s.update()) {
              deletes.push(s);
            }
          }
        });
        deletes.forEach((s) => deleteArrayItem(SVGShapes.value, s));
      };
      canvasFrame = (d) => {
        const deletes = [];
        CanvasShapes.value.forEach((s) => {
          if (s instanceof ConfettiShapeCombo) {
            if (!s.update()) {
              deletes.push(s);
            }
          }
        });
        deletes.forEach((s) => deleteArrayItem(CanvasShapes.value, s));
      };
    }
  },
  {
    name: "LinePicture",
    onClick: () => {
      currentType.value = "LinePicture";
      const redPoint = new BallShape({
        x: 0,
        y: 0,
        ballRadius: 5,
        attrs: { fill: "rgba(255,0,0,1)" }
      });
      const redPointClone = redPoint.clone();
      const greenPoint = new BallShape({
        x: 0,
        y: 0,
        ballRadius: 3,
        attrs: { fill: "rgba(0,255,0,0.8" }
      });
      const greenPointClone = greenPoint.clone();
      const boundingRect = new RectShape({
        x: 0,
        y: 0,
        rectWidth: 0,
        rectHeight: 0,
        attrs: { fill: "none", stroke: "#0000ff", strokeWidth: "2" }
      });
      const boundingRectClone = boundingRect.clone() as RectShape;
      SVGShapes.value = [linePicture, boundingRect, redPoint, greenPoint];
      CanvasShapes.value = [
        linePictureClone,
        boundingRectClone,
        redPointClone,
        greenPointClone
      ];
      svgStart = (d) => {
        let prevColor;
        let prevShape;
        d.shapeTap$.subscribe((s) => {
          if (!s) {
            return;
          }
          if (!prevColor) {
            prevColor = s.getAttr("fill");
          }
          if (prevShape && prevShape !== s) {
            prevShape.setAttr("fill", prevColor);
            prevColor = s.getAttr("fill");
          }
          s.setAttr("fill", "pink");
          prevShape = s;
          const rect = s.getRealBoundingClientRect();
          boundingRect.update({
            rectWidth: rect.width,
            rectHeight: rect.height
          });
          boundingRect.moveTo(rect.x, rect.y);
        });
      };
      canvasStart = (d) => {
        let prevColor;
        let prevShape;
        d.shapeTap$.subscribe((s) => {
          if (!s) {
            return;
          }
          if (!prevColor) {
            prevColor = s.getAttr("fill");
          }
          if (prevShape && prevShape !== s) {
            prevShape.setAttr("fill", prevColor);
            prevColor = s.getAttr("fill");
          }
          s.setAttr("fill", "pink");
          prevShape = s;
          const rect = s.getRealBoundingClientRect();
          boundingRectClone.update({
            rectWidth: rect.width,
            rectHeight: rect.height
          });
          boundingRectClone.moveTo(rect.x, rect.y);
        });
      };
      let t1 = 0;
      let t2 = 0;
      const tGap = 0.01;
      let interpolationPathIndex1 = 0;
      let interpolationPathIndex2 = 0;
      let totalLengthOfPath1 =
        linePicture.shapes[interpolationPathIndex1].getTotalLength();
      let totalLengthOfPath2 =
        linePictureClone.shapes[interpolationPathIndex2].getTotalLength();
      svgFrame = (d) => {
        if (t1 > 1) {
          interpolationPathIndex1++;
          t1 = 0;
          if (interpolationPathIndex1 > linePicture.shapes.length - 1) {
            interpolationPathIndex1 = 0;
          }
          totalLengthOfPath1 =
            linePicture.shapes[interpolationPathIndex1].getTotalLength();
        }
        t1 += tGap;
        // 一种方式
        let { x: x1, y: y1 } = linePicture.shapes[
          interpolationPathIndex1
        ].getPointAtLength(t1 * totalLengthOfPath1);
        redPoint.moveTo(x1, y1);
        // 另一种方式
        let { x: x2, y: y2 } =
          linePicture.shapes[interpolationPathIndex1].interpolate(t1);
        greenPoint.moveTo(x2, y2);
      };
      canvasFrame = (d) => {
        if (t2 > 1) {
          interpolationPathIndex2++;
          t2 = 0;
          if (interpolationPathIndex2 > linePictureClone.shapes.length - 1) {
            interpolationPathIndex2 = 0;
          }
          totalLengthOfPath2 =
            linePictureClone.shapes[interpolationPathIndex2].getTotalLength();
        }
        t2 += tGap;
        // 一种方式
        let { x: x1, y: y1 } = linePictureClone.shapes[
          interpolationPathIndex2
        ].getPointAtLength(t2 * totalLengthOfPath2);
        redPointClone.moveTo(x1, y1);
        // 另一种方式
        let { x: x2, y: y2 } =
          linePictureClone.shapes[interpolationPathIndex2].interpolate(t2);
        greenPointClone.moveTo(x2, y2);
      };
    }
  },
  {
    name: "StrokeDash",
    onClick: () => {
      currentType.value = "StrokeDash";
      SVGShapes.value = [strokeDashPath];
      CanvasShapes.value = [strokeDashPath];
      svgStart = (d) => {};
      canvasStart = (d) => {};
      svgFrame = (d) => {};
      canvasFrame = (d) => {};
    }
  },
  {
    name: "strokeDashTween",
    onClick: () => {
      currentType.value = "strokeDashTween";
      SVGShapes.value = [strokeDashPath];
      CanvasShapes.value = [strokeDashPath];
      svgStart = (d) => {
        strokeDashPath.strokeDashTween(4000, {
          // easeFunction: props.easeFunction
        });
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {};
      canvasFrame = (d) => {};
    }
  },
  {
    name: "strokeDashTweenSequence",
    onClick: () => {
      currentType.value = "strokeDashTweenSequence";
      const s = linePicture.clone();
      s.setAttr({
        stroke: "none",
        strokeWidth: "4",
        fill: "none"
      });
      SVGShapes.value = [s];
      CanvasShapes.value = [s];
      svgStart = (d) => {
        s.setAttr({
          stroke: "#ff0000"
        });
        s.strokeDashTween(300, {
          type: "sequence",
          // easeFunction: props.easeFunction,
          onGroupStart: () => {}
        });
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {};
      canvasFrame = (d) => {};
    }
  },
  {
    name: "strokeDashTweenParallel",
    onClick: () => {
      currentType.value = "strokeDashTweenParallel";
      const s = linePicture.clone();
      s.setAttr({
        stroke: "none",
        strokeWidth: "4",
        fill: "none"
      });
      SVGShapes.value = [s];
      CanvasShapes.value = [s];
      svgStart = (d) => {
        s.setAttr({
          stroke: "#ff0000"
        });
        s.strokeDashTween(3000, {
          type: "parallel",
          // easeFunction: props.easeFunction,
          onGroupStart: () => {}
        });
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {};
      canvasFrame = (d) => {};
    }
  },
  {
    name: "线条动画",
    onClick: () => {
      currentType.value = "线条动画";
      const ball = new BallShape({
        x: 100,
        y: 100,
        ballRadius: 4,
        attrs: {
          stroke: Color.random().toString()
        }
      });
      const arrow = new ArrowShape({
        x: 200,
        y: 200,
        attrs: {
          stroke: Color.random().toString()
        }
      });
      const rect = new RectShape({
        rectWidth: 100,
        rectHeight: 60,
        rectBorderRadius: 10,
        x: 150,
        y: 170,
        attrs: {
          stroke: Color.random().toString()
        }
      });
      const fox = new FoxShape({
        x: 200,
        y: 200,
        scale: 3,
        attrs: {
          stroke: Color.random().toString()
        }
      });
      const before = PointsShape.generate(100, 100, 100, 3);
      before.moveTo(100, 100);
      before.setAttr({
        stroke: Color.random().toString()
      });
      const after = PointsShape.generate(100, 100, 100, 6);
      after.moveTo(100, 100);
      after.setAttr({
        stroke: Color.random().toString()
      });
      const shapes = [before, after, ball, arrow, rect, fox];
      SVGShapes.value = [];
      CanvasShapes.value = [];
      svgStart = (d) => {
        const s = polymorphTween(shapes, 6000);
        // s.enableDrag = true;
        // s.attrs.stroke = "#ff0000";
        SVGShapes.value.unshift(s);
        CanvasShapes.value.unshift(s);
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {};
      canvasFrame = (d) => {};
    }
  },
  {
    name: "线条动画Combo",
    onClick: () => {
      currentType.value = "线条动画Combo";
      const arrow = new ArrowShape({
        x: 200,
        y: 200,
        attrs: {
          stroke: Color.random().toString()
        }
      });
      const rect = new RectShape({
        rectWidth: 100,
        rectHeight: 60,
        rectBorderRadius: 10,
        x: 150,
        y: 170,
        attrs: {
          stroke: Color.random().toString()
        }
      });
      const fox = new FoxShape({
        x: 200,
        y: 200,
        scale: 3,
        attrs: {
          stroke: Color.random().toString()
        }
      });
      const before = PointsShape.generate(100, 100, 100, 3);
      before.moveTo(100, 100);
      before.setAttr({
        stroke: Color.random().toString()
      });
      const after = PointsShape.generate(100, 100, 100, 6);
      after.moveTo(100, 100);
      after.setAttr({
        stroke: Color.random().toString()
      });
      const ship = new ShipShapeCombo({
        showFlame: true,
        attrs: {
          stroke: Color.random().toString()
        }
      });
      ship.comboMove(400, 100);
      ship.comboRotate(30);
      ship.comboScale(2);
      console.log(ship.shapes[0].transformPathD().getPolymorphPoints());
      // const shapes = [before, after, arrow, rect, ship, fox];
      const shapes = [linePicture, ship];
      // const shapes = [before, after, arrow, rect, linePicture, ship, fox];
      SVGShapes.value = [];
      CanvasShapes.value = [];
      // SVGShapes.value = [ship];
      // CanvasShapes.value = [ship];
      svgStart = (d) => {
        const s = polymorphTween(shapes, 10000, {
          shapeOrCombo: (s, t) => {
            if (t == 0 || withinErrorMarginNumber(0.05)(t, 0.5) || t == 1) {
              console.log("t =", t);
              // ship-flame
              console.log(
                s.shapes[0].transformPathD().getPolymorphPoints(),
                s.shapes[0].pathD().prevPathX,
                s.shapes[0].pathD().prevPathY,
                s.shapes[0].transformPathD().prevPathX,
                s.shapes[0].transformPathD().prevPathY
              );
              // console.log(toObjectString(s));
            }
          }
        });
        // s.enableDrag = true;
        // s.attrs.stroke = "#ff0000";
        SVGShapes.value.unshift(...s.shapes);
        CanvasShapes.value.unshift(...s.shapes);
      };
      canvasStart = (d) => {};
      svgFrame = (d) => {};
      canvasFrame = (d) => {};
    }
  }
];
</script>

<template>
  <Page>
    <FPSStats></FPSStats>

    <div class="btns">
      <Button
        v-for="item in cases"
        :key="item.name"
        :item="item"
        @click="item.onClick"
        :stylePattern="currentType == item.name ? 'fullfill' : 'outline'"
      >
        {{ item.name }}
      </Button>
    </div>

    <div
      v-show="currentType == 'StrokeDash'"
      class="btns sub-btns"
    >
      <div style="width: 100%">
        <div>stroke-dasharray: {{ dasharray }},{{ dasharray }}</div>
        <InputRange
          :max="totalLength"
          :defaultValue="dasharray"
          @change="changeDasharray"
        ></InputRange>
      </div>
      <div style="width: 100%">
        <div>stroke-dashoffset: {{ dashoffset }}</div>
        <InputRange
          :max="totalLength"
          :defaultValue="dashoffset"
          @change="changeDashoffset"
        ></InputRange>
      </div>
    </div>

    <Box
      type="svg"
      :shapes="SVGShapes"
      :loop="true"
      style="width: 100%; height: 300px; background-color: rgba(0, 0, 0, 0.1)"
      @start="svgStart"
      @frame="svgFrame"
    ></Box>

    <Box
      type="canvas"
      :shapes="CanvasShapes"
      :loop="true"
      style="width: 100%; height: 300px; background-color: rgba(0, 0, 0, 0.2)"
      @start="canvasStart"
      @frame="canvasFrame"
    ></Box>
  </Page>
</template>

<style scoped>
.btns {
  display: flex;
  flex-wrap: wrap;
}
.btns > * {
  margin-right: 10px;
  margin-bottom: 10px;
}
.sub-btns {
  padding-top: 10px;
  background-color: #dbffff;
}
</style>
